import * as Backbone from "backbone";
import * as _ from "lodash";
import * as moment from "moment";

export interface ChannelRecord {
    channel_allow_select_decimals: number;
    channel_based_on_id: number;
    channel_conversion_function_id: number;
    channel_converted_value: number;
    channel_decimals: number;
    channel_func: string;
    channel_id: number;
    channel_last_timestamp: number;
    channel_name: string;
    channel_position: number;
    channel_received_timestamp: number;
    channel_secret: string;
    channel_stl_value: number;
    channel_unit: string;
    channel_unit_manually_chosen: boolean;
    channel_virtual: boolean;
    module_polling_frequency: number;
}

export interface SensorRecord {
    channels: ChannelRecord[];
    sensor_connector: number;
    sensor_firmware_version: number;
    sensor_id: number;
    sensor_last_seen: number;
    sensor_name: string;
    sensor_type: number;
    sensor_version: number;
    is_outdoors?: boolean;
}

export interface ModuleRecord {
    install_id: number;
    module_gateway_sn: string;
    module_id: number;
    module_is_gateway: number;
    module_location: string;
    module_number: string;
    module_polling_frequency: number;
    sensors: SensorRecord[],
    module_virtual : number
}

export class Module extends Backbone.Model {
    attributes: ModuleRecord;

    getChannelValue(connector: number, position: number, _default: any, key?: string) {
        var con = this.getConnector(connector);
        if (!key) key = 'channel_converted_value';

        if (con) {
            var pos = _.find(con['channels'], {'channel_position': position});
            return pos && pos[key] !== null ? pos[key] : _default
        }
        return _default
    }

    isGateway() {
        return Boolean(this.get('module_is_gateway'));
    }

    getSensorLastTimestamp(connector: number, key: string) {
        var max = -Infinity;
        if (connector === 0) {
            // Loop through all channels to find the maximum for the module
            var sensors = this.get('sensors');
            for (var i = 0; i < sensors.length; i++) {
                for (var j = 0; j < sensors[i].channels.length; j++) {
                    if ((sensors[i].channels[j][key] > max) && !sensors[i].channels[j]["channel_virtual"]) max = sensors[i].channels[j][key];
                }
            }
        } else {
            // Loop though the sensor's channels and check the sensor's config hash from internal sensors
            // for sensors that don't have channels
            if (connector < 4) max = this.getChannelValue(0, 10 + connector * 8, -Infinity, key);

            var con = this.getConnector(connector);
            if (con) {
                // Make sure conver sensors without any channels
                if (key === 'channel_received_timestamp' && !con.channels.length) return con.sensor_last_seen;

                for (var i = 0; i < con.channels.length; i++) {
                    if ((con.channels[i][key] > max) && !con.channels[i]["channel_virtual"]) max = con.channels[i][key];
                }
            }
        }
        return max;
    }

    getSensorLastSentTimestamp(connector: number) {
        return this.getSensorLastTimestamp(connector, 'channel_last_timestamp')
    }

    getSensorLastReceivedTimestamp(connector: number) {
        return this.getSensorLastTimestamp(connector, 'channel_received_timestamp')
    }

    isOnline():boolean {
        return this.connectorInUse(0)
    }

    /* Checks if the given connector is currently in use.
     * If connector == 0 this will return whether the module is online
     * If relativeToModule == true we will check whether the given connector was in use at the time the module was
     * last seen
     */
    connectorInUse(connector: number, relativeToModule?: boolean):boolean {
        var twoStatusPollsAgo;
        var packeddatamode = this.getPackedDataMode();

        // We modify the multiplier if we use packed data.
        if(packeddatamode == 2 || packeddatamode == 4){ //For Temp and Humidity (2) as packed data or bridge (4; assume packeddata = 1 and internalsensor = 0)
            var multiplier = 20;        // 39/2 + 1 = 20
        }else if (packeddatamode == 3){ // 39/3 + 1 = 14
            var multiplier = 14;
        }else if(packeddatamode == 1){  // 39/1 + 1 = 40
            var multiplier = 40;
        }else{ // all other cases
            var multiplier = 5;
        }

        if (relativeToModule) {
            twoStatusPollsAgo = Math.max(
                this.getSensorLastSentTimestamp(0), this.getSensorLastReceivedTimestamp(0), 0
            );
        } else {
            twoStatusPollsAgo = moment.utc().unix();
        }
        if(packeddatamode) { //Default value is 5, except if we are in packed mode where it becomes 20 (worst case scenario)
            twoStatusPollsAgo -= this.getPollingFrequency() * multiplier; //we use this in case poll freq < than 60sec
        }else{
            twoStatusPollsAgo -= this.getStatusPollingFrequency() * multiplier;
        }

        // If the sensor was never seen before, ts will be 0
        var ts = Math.max(
            this.getSensorLastSentTimestamp(connector), this.getSensorLastReceivedTimestamp(connector), 0
        );
        return ts >= Math.max(twoStatusPollsAgo, 0);
    }

    moduleIsUploadingData():boolean {
        var sent = this.getSensorLastSentTimestamp(0);
        var received = this.getSensorLastReceivedTimestamp(0);
        var multiplier = 5;

        return sent === -Infinity ? false : sent + (multiplier * this.getStatusPollingFrequency()) < received;
    }

    showConnector(connector):boolean {
        // If the module is up, determine if the connector is in use relative to now
        // If the module is down determine if the connector was in use relative to the Module's last down time
        var moduleUp = this.connectorInUse(0);
        return moduleUp ? this.connectorInUse(connector) : this.connectorInUse(connector, true);
    }

    getStatusPollingFrequency():number {
        return Math.max(this.getPollingFrequency(), 60);
    }

    getPollingFrequency():number {
        // First check what the module reports, and if it does not have a value (gen 1) use the
        // value coming from the aggregation
        return this.getChannelValue(0, 6, this.getChannelValue(0, 2, 60, 'module_polling_frequency'));
    }

    getPackedDataMode():number {
        // We will only have values for this for ver5 and after, otherwise it will use default
        return this.getChannelValue(0, 49, 0);
    }

    getConnector(connector: number): SensorRecord {
        // Get the internal sensor so we can check what sensor type the node reports
        var internalSensor = <SensorRecord> _.find(this.get('sensors'), {'sensor_connector': 0});

        // Some broken modules that have not communicated with the server in a long time don't have any sensors
        if (!internalSensor) {
            var sensorWithoutInternalSensor = <SensorRecord> _.find(this.get('sensors'), {'sensor_connector': connector});
            if(!sensorWithoutInternalSensor) return;
        }

        // If we wanted the internal sensor, we're done
        if (connector == 0) return internalSensor;

        // Check what sensor type the node reports
        var sensorTypePosition = (connector * 8) + 6;
        var sensorType = null;

        if(!this.isVirtualModule() && internalSensor) {
            var channel = <ChannelRecord> _.find(internalSensor['channels'], {channel_position: sensorTypePosition});
            if (channel) sensorType = channel.channel_converted_value;
        }
        // Get all sensors adn find the ones with matching type
        var sensors = <SensorRecord[]> _.filter(this.get('sensors'), {'sensor_connector': Number(connector)});
        var sensorsWithMatchingType = <SensorRecord[]> _.filter(sensors, {sensor_type: sensorType});

        var length = sensors.length;
        if (!length) {
            return null;
        } else if (length === 1) {
            return sensors[0]
        } else if (sensorsWithMatchingType.length) {
            return _.maxBy(sensorsWithMatchingType, function (s) {
                return s.sensor_last_seen
            });
        } else {
            return _.maxBy(sensors, function (s) {
                return s.sensor_last_seen
            });
        }
    }

    hasSensors(): boolean {
        if (this.get('sensors').length) {
            return _.some(_.map([1, 2, 3, 4], (connector) => {
                return Boolean(this.getConnector(connector))
            }));
        } else {
            return false;
        }
    }

    getSensorFWVersion(connector: number) {
        var con = this.getConnector(connector);
        if (con) {
            var ver = Number(con['sensor_firmware_version']);
            if (ver) {
                return "ver " + Math.floor(ver / 100) + '.' + Math.floor(ver % 100 / 10) + '.' + Math.floor(ver % 10);

                // Gen 2 published internal sensors should have the same FW version as the unpublished internal sensor
            } else if (Number(connector) === 4 && con['sensor_type'] === 1) {
                return this.getSensorFWVersion(0);

                // Gen 1 does not publish a fw version
            } else {
                return 'gen 1'
            }
        }
    }

    getSensorVersion(connector: number):number {
        var con = this.getConnector(connector);
        if (con) {
            return Number(con['sensor_version']);
        }
    }

    getBatteryV() {
        return this.getChannelValue(0, 1, false);
    }

    getChannelId(connector: number, position: number):number {
        var con = this.getConnector(connector);
        if (con) {
            var chan = _.find(con['channels'], {channel_position: position});
            return chan ? chan['channel_id'] : null;
        }
        return null
    }

    getChannelById(channelId: number) {
        for (var i = 0; i < 5; i++) {
            var con = this.getConnector(i);
            if (con) {
                var chan = _.find(con['channels'], {channel_id: channelId});
                if (chan) return _.extend({
                    sensor_type: con['sensor_type'],
                    sensor_id: con['sensor_id'],
                    sensor_connector: con['sensor_connector'],
                    sensor_version: con['sensor_version'],
                    sensor_name: con['sensor_name']
                }, chan);
            }
        }
    }

    getChannels() {
        var channels = [];
        for (var i = 0; i < 5; i++) {
            var con = this.getConnector(i);
            if (con) {
                _.each(con['channels'], (chan)=> {
                    channels.push(_.extend({
                        sensor_type: con['sensor_type'],
                        sensor_id: con['sensor_id'],
                        sensor_connector: con['sensor_connector'],
                        sensor_version: con['sensor_version'],
                        module_location: this.get('module_location'),
                        module_gateway_sn: this.get('module_gateway_sn'),
                        module_id: this.get('module_id'),
                        module_is_gateway: this.get('module_is_gateway'),
                        module_number: this.get('module_number'),
                    }, chan));
                });
            }
        }
        return channels;
    }

    getSensorById(sensorId: number): SensorRecord {
        return <SensorRecord> _.find(this.get('sensors'), {sensor_id: sensorId});
    }

    getBatteryChanId() {
        return this.getChannelId(0, 1);
    }

    getHumidityChanId() {
        return this.getChannelId(0, 3);
    }

    getTemperatureChanId() {
        return this.getChannelId(0, 2);
    }

    getLinkQualityChanId() {
        return this.getChannelId(0, 0);
    }

    getTemperatureF() {
        return this.getChannelValue(0, 2, false);
    }

    getHumidityPct() {
        return this.getChannelValue(0, 3, false);
    }

    getLinkQuality() {
        return this.getChannelValue(0, 0, false);
    }

    getChargeStateText() {
        var chargeState = this.getChannelValue(0, 12, '-');
        switch (chargeState) {
            case 0:
                return "<span data-toggle='tooltip' data-placement='top' title='Error'>" +
                    "<i class='fa fa-plug text-danger'></i></span>";
            case 1:
                return "<span data-toggle='tooltip' data-placement='top' title='Outlet powered, no battery'>" +
                    "<i class='fa fa-plug'></i></span>";
            case 2:
                return "<span data-toggle='tooltip' data-placement='top' title='Outlet powered, battery unplugged'>" +
                    "<i class='fa fa-battery-0 text-danger'></i>&nbsp;&nbsp;&nbsp;<i class='fa fa-plug'></i>" +
                    "</span>";
            case 3:
                return "<span data-toggle='tooltip' data-placement='top' title='Outlet powered, battery charging'>" +
                    "<i class='fa fa-bolt'>&nbsp;<i class='fa fa-battery-2'>" +
                    "</i>&nbsp;&nbsp;&nbsp;<i class='fa fa-plug'></i>" +
                    "</span>";
            case 4:
                return "<span data-toggle='tooltip' data-placement='top' title='Outlet powered, battery charged'>" +
                    "<i class='fa fa-battery-full'></i>&nbsp;&nbsp;&nbsp;<i class='fa fa-plug'></i></span>";
            case 5:
                return "<span data-toggle='tooltip' data-placement='top' title='Power down, on battery'>" +
                    "<i class='fa fa-battery-3'></i></span>";
            case 6:
                return "<span data-toggle='tooltip' data-placement='top' title='Power down, on battery (low)'>" +
                    "<i class='fa fa-battery-1 text-danger'>" +
                    "</span>";
            case 7:
                return "<span data-toggle='tooltip' data-placement='top' title='Charge Fault'>" +
                    "<i class='fa fa-bolt text-danger'></i>&nbsp;<i class='fa fa-battery-2 text-danger'></i>" +
                    "&nbsp;&nbsp;&nbsp;<i class='fa fa-plug'></i></span>";
            case 8:
                return "<span data-toggle='tooltip' data-placement='top' title='Outlet powered, AAA battery' class='pull-left'>" +
                    "<span class='pull-left' style='display: inline-block; margin-right: 8px;'><i class='fa fa-battery-4 status-sidepanel-tripple-a'></i><i class='fa fa-battery-4 status-sidepanel-tripple-a'></i><i class='fa fa-battery-4 status-sidepanel-tripple-a'></i>" +
                    "</span><span class='pull-left'><i class='fa fa-plug'></i>" +
                    "</span></span>";
            case 9:
                return "<span data-toggle='tooltip' data-placement='top' title='AAA battery'>" +
                    "<i class='fa fa-battery-3 status-sidepanel-tripple-a'></i><i class='fa fa-battery-3 status-sidepanel-tripple-a'></i><i class='fa fa-battery-3 status-sidepanel-tripple-a'></i>" +
                    "</span>";
            case 10:
                return "<span data-toggle='tooltip' data-placement='top' title='AAA battery (low)'>" +
                    "<i class='fa fa-battery-1 status-sidepanel-tripple-a text-danger'></i><i class='fa fa-battery-1 status-sidepanel-tripple-a text-danger'></i><i class='fa fa-battery-1 status-sidepanel-tripple-a text-danger'></i>" +
                    "</span>";
        }
    }

    getNodeModeText() {
        var val = this.getChannelValue(0, 9, false);
        if (val === false) {
            return '-';
        } else if(val === 0){
            return "Endpoint";
        }else if(val === 1){
            return "Relay";
        }else if(val === 3){
            return "Endpoint Relay";
        }else{
            return "-";
        }
    }

    getSDModeText() {
        var val = this.getChannelValue(0, 11, false);
        if (val === false) {
            return 'No Data';
        } else if ((val & 1) !== 0) {
            // SD present
            var rtn = '';
            if ((val & 2) !== 0) {
                rtn += 'Working <br>';
            } else {
                rtn += 'Not Working <br>';
            }

            if ((val & 4) !== 0) {
                rtn += 'Not Empty';
            } else {
                rtn += 'Empty';
            }
        } else {
            rtn = 'Not Present';
        }
        return "<span class='pull-left'>" + rtn + "</span>"
    }

    getConnectorResetTypeText(connector) {
        var channel = connector === 0 ? 10 : (9 + connector * 8);
        var resetType = this.getChannelValue(0, channel, '-');

        switch (resetType) {
            case 7:
                return 'External';
            case 6:
                return 'Software';
            case 5:
                return 'Watchdog';
            case 4:
                return 'Sleep';
            case 3:
                return 'Idle';
            case 2:
                return 'Power-on';
            case 1:
                return 'Brown-out';
            default:
                return '-';
        }
    }

    getNodeResetTypeAbbr() {
        return this.getChannelValue(0, 10, '-')
    }

    getConnectorLastSeenText(connector) {
        var lastTime = this.getSensorLastSentTimestamp(connector);
        if (lastTime === -Infinity || lastTime === 0 || lastTime === null) return '';
        return moment(lastTime * 1000).format('L LTS');
    }

    isBatteryPowered() {
        var chargeState = this.getChannelValue(0, 12, 0);
        return (chargeState === 9       // AAA battery
            || chargeState === 10)      // AAA battery (low)
    }

    //Returns untranslated Node mode, channel value of 0 is endpoint, value of 1 is relay, 2 is gateway, 3 is endpoint relay
    //But configuration values are 0 for Node chooses, 2 for force endpoint, 3 for force node, 4 for force endpoint relay
    getNodeMode() {
        return this.getChannelValue(0, 9, 1);
    }

    actsLikeBatteryPowered() {
        return this.isBatteryPowered() || this.getNodeMode() === 0 || this.getNodeMode() === 3;
    }

    getIPText() {
        var ipv4 = this.getIPv4String();
        var ipv6 = this.getIPv6String();

        if(!ipv6 && ipv4){
            return ipv4
        } else if(!ipv4 && ipv6) {
            return ipv6
        } else if(!ipv6 && !ipv4) {
            return null
        } else {
            // both exist. Check which is newer
            var ipv4LastSeen = this.getChannelValue(0, 43, 0, 'channel_last_timestamp');
            var ipv6LastSeen = this.getChannelValue(0, 44, 0, 'channel_last_timestamp');
            return ipv4LastSeen > ipv6LastSeen ? ipv4LastSeen : ipv6LastSeen;
        }
    }

    getIPv4String () {
        var ipv4 = this.getChannelValue(0, 43, null);
        if(!ipv4) return null;

        var buf = new Buffer(4);
        buf.writeUInt32BE(ipv4, 0);
        return buf.toJSON().data.join('.');
    }

    getIPv6String () {
        var buf = new Buffer(16);
        for(var i=0; i<4; i++){
            var s = this.getChannelValue(0, 44 + i, null);
            if(!s) return null;

            buf.writeUInt32BE(s, i * 4);
        }

        return buf.toString('hex').match(/.{1,5}/g).join(':');
    }

    isVirtualModule() {
        return this.get('module_virtual') === 1 ;
    }

    getDisplayName() {
        if(!this.isVirtualModule()) {
            return this.get('module_number') + ' - ' + this.get('module_location');
        }
        return this.get('module_location');
    }
}
Module.prototype.idAttribute = 'module_id';



// WEBPACK FOOTER //
// ./srv/public/js/models/Module.ts